import { CheckableField, CheckableFields, FormField, Heading, TextInput } from '@msaf/core-react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { FormFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'

interface WellHeadSecurityDetailsProps {
  isLoading: boolean
  fields: Partial<FormFieldsEdit>
  validationErrors: ValidationErrors
  setValue: SetValue<FormFieldsEdit>
}

export default function EditWellHeadSecuritySurroundingArea({
  isLoading,
  fields,
  setValue,
  validationErrors,
}: WellHeadSecurityDetailsProps) {
  return (
    <>
      <Heading level={3}>Surrounding area</Heading>

      <FormField
        validationMessages={validationErrors?.wellheadCovering}
        isSkeleton={isLoading}
        labelId='wellheadCoveringLabel'
        label='Covering over wellhead'
        toolTipMessage={TOOLTIP.wellheadCovering}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'wellheadCoveringLabel'}
          fieldId='wellheadCovering'
          value={`${fields.wellheadCovering ?? ''}`}
          setValue={({ value }) => setValue('wellheadCovering', value)}
          isInvalid={!!validationErrors.wellheadCovering}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.coveringSecure}
        isSkeleton={isLoading}
        labelId='coveringSecureLabel'
        label='Covering secure'
        toolTipMessage={TOOLTIP.coveringSecure}
      >
        <CheckableFields ariaLabelledBy='coveringSecureLabel' isHorizontal>
          <CheckableField
            id='coveringSecureYes'
            name='coveringSecure'
            describedBy='coveringSecureLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.coveringSecure ?? false}
            onChange={() => setValue('coveringSecure', true)}
          />
          <CheckableField
            id='coveringSecureNo'
            name='coveringSecure'
            describedBy='coveringSecureLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.coveringSecure === false}
            onChange={() => setValue('coveringSecure', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.isWellHeadFenced}
        isSkeleton={isLoading}
        labelId='isWellHeadFencedLabel'
        label='Fence around wellhead (5m radius)'
      >
        <CheckableFields ariaLabelledBy='isWellHeadFencedLabel' isHorizontal>
          <CheckableField
            id='isWellHeadFencedYes'
            name='isWellHeadFenced'
            describedBy='isWellHeadFencedLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellHeadFenced ?? false}
            onChange={() => setValue('isWellHeadFenced', true)}
          />
          <CheckableField
            id='isWellHeadFencedNo'
            name='isWellHeadFenced'
            describedBy='isWellHeadFencedLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellHeadFenced === false}
            onChange={() => setValue('isWellHeadFenced', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.stockAccess}
        isSkeleton={isLoading}
        labelId='stockAccessLabel'
        label='Stock access to wellhead'
        toolTipMessage={TOOLTIP.stockAccess}
      >
        <CheckableFields ariaLabelledBy='stockAccessLabel' isHorizontal>
          <CheckableField
            id='stockAccessYes'
            name='stockAccess'
            describedBy='stockAccessLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.stockAccess ?? false}
            onChange={() => setValue('stockAccess', true)}
          />
          <CheckableField
            id='stockAccessNo'
            name='stockAccess'
            describedBy='stockAccessLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.stockAccess === false}
            onChange={() => setValue('stockAccess', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.wellheadTopography}
        isSkeleton={isLoading}
        labelId='wellheadTopographyLabel'
        label='Topography around wellhead'
        toolTipMessage={TOOLTIP.wellheadTopography}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'wellheadTopographyLabel'}
          fieldId='wellheadTopography'
          value={`${fields.wellheadTopography ?? ''}`}
          setValue={({ value }) => setValue('wellheadTopography', value)}
          isInvalid={!!validationErrors.wellheadTopography}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.septicTankPresent}
        isSkeleton={isLoading}
        labelId='septicTankPresentLabel'
        label='Septic tank on property'
      >
        <CheckableFields ariaLabelledBy='septicTankPresentLabel' isHorizontal>
          <CheckableField
            id='septicTankPresentYes'
            name='septicTankPresent'
            describedBy='septicTankPresentLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.septicTankPresent ?? false}
            onChange={() => setValue('septicTankPresent', true)}
          />
          <CheckableField
            id='septicTankPresentNo'
            name='septicTankPresent'
            describedBy='septicTankPresentLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.septicTankPresent === false}
            onChange={() => setValue('septicTankPresent', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.distanceToNearestSepticTank}
        isSkeleton={isLoading}
        htmlFor='distanceToNearestSepticTank'
        label='Distance to nearest septic tank (m)'
      >
        <TextInput
          fieldSize={InputWidth.medium}
          id={'distanceToNearestSepticTank'}
          isSkeleton={isLoading}
          value={`${fields?.distanceToNearestSepticTank ?? ''}`}
          setValue={(value) => setValue('distanceToNearestSepticTank', value)}
          isInvalid={!!validationErrors.distanceToNearestSepticTank}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.wellheadHazards}
        isSkeleton={isLoading}
        htmlFor='wellheadHazards'
        label='Hazards around wellhead'
        toolTipMessage={TOOLTIP.wellheadHazards}
      >
        <TextInput
          id={'wellheadHazards'}
          isSkeleton={isLoading}
          value={`${fields?.wellheadHazards ?? ''}`}
          setValue={(value) => setValue('wellheadHazards', value)}
          isInvalid={!!validationErrors.wellheadHazards}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.surroundingLanduse}
        isSkeleton={isLoading}
        labelId='surroundingLanduseLabel'
        label='Surrounding landuse'
        toolTipMessage={TOOLTIP.surroundingLanduse}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'surroundingLanduseLabel'}
          fieldId='surroundingLanduse'
          value={`${fields.surroundingLanduse ?? ''}`}
          setValue={({ value }) => setValue('surroundingLanduse', value)}
          isInvalid={!!validationErrors.surroundingLanduse}
        />
      </FormField>
    </>
  )
}
